import { useRouter } from 'next/router'
import { toast } from 'react-hot-toast'
import {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useRef,
  useState,
  MutableRefObject,
} from 'react'
import { ButtonWithIcon, InlineInputWithLabel } from '@/components/input'
import { PageHeader } from '@/components/PageHeader'
import { useNote, MultipleRecipients } from 'contexts/note'
import NoteLayout from 'layouts/NoteLayout'
import { CheckIcon, UserGroupIcon } from '@heroicons/react/20/solid'
import { PlusIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { addRecipient } from '@/common/recipients'
import { handleAPIRequest, localAPIRequest } from '@/common/request'
import { PageSection } from '@/components/PageSection'
import { createRecipientLinkWithID, logEvent } from '@/common/utils'
import { RadioGroup } from '@headlessui/react'
import { PhoneInput, PhoneInputData } from '@/components/PhoneInput'
import { SectionFooter } from '@/components/SectionFooter'
import { EVENTS } from '@/common/events'

type Method = {
  id: string
  desc: string
  label: string
}

export const methods: Method[] = [
  {
    id: 'email',
    label: 'By Email',
    desc: 'Recommended, if they frequently use email',
  },
  {
    id: 'phone',
    label: 'By Phone',
    desc: 'If they prefer chat applications such as Whatsapp, etc.',
  },
]

const steps = {
  method: 1,
  contactInfo: 2,
  complete: 3,
}

export function Step1Method({
  value,
  onChange,
}: {
  value: Method
  onChange: (method: Method) => void
}) {
  return (
    <div>
      <RadioGroup className="space-y-4" value={value} onChange={onChange}>
        <RadioGroup.Label className="sr-only">Send the note</RadioGroup.Label>
        {methods.map((plan) => (
          <RadioGroup.Option
            key={plan.id}
            value={plan}
            className={({ checked }) =>
              `${
                checked
                  ? 'bg-white dark:bg-gray-700 shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800'
              } border-colored relative flex cursor-pointer rounded-md px-4 py-4 focus:outline-none`
            }
          >
            {({ checked }) => (
              <>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-sm">
                      <RadioGroup.Label as="div" className={`font-medium`}>
                        {plan.label}
                      </RadioGroup.Label>
                      <RadioGroup.Description
                        className={`mt-1 text-gray-500 dark:text-gray-400`}
                      >
                        {plan.desc}
                      </RadioGroup.Description>
                    </div>
                  </div>
                  {checked && (
                    <div className="shrink-0 text-blue-500">
                      <CheckIcon className="h-6 w-6" />
                    </div>
                  )}
                </div>
              </>
            )}
          </RadioGroup.Option>
        ))}
      </RadioGroup>
    </div>
  )
}

export const isExistingRecipient = (
  recipients: MultipleRecipients,
  email: string | null,
  full_phone: PhoneInputData['full_phone']
) => {
  return recipients?.find(
    (rec) =>
      (email && rec?.email == email) ||
      (full_phone && rec?.full_phone == full_phone)
  )
}

function EmailInput({
  emailRef,
}: {
  emailRef: MutableRefObject<string | null>
}) {
  const [email, setEmail] = useState(emailRef?.current ?? '')

  return (
    <InlineInputWithLabel
      type="email"
      labelFor="email"
      label="Their email address"
      placeholder="recipient@example.com"
      value={email}
      onChange={(e) => {
        const value = e.target.value
        emailRef.current = value
        setEmail(value)
      }}
    />
  )
}

function NameInput({ nameRef }: { nameRef: MutableRefObject<string | null> }) {
  const [name, setName] = useState(nameRef?.current ?? '')

  return (
    <InlineInputWithLabel
      // @ts-ignore
      required
      type="text"
      value={name}
      label="Their name"
      autoComplete="off"
      placeholder="John Smith"
      labelFor="recipient_name"
      onChange={(e) => {
        const value = e.target.value
        nameRef.current = value
        setName(value)
      }}
    />
  )
}

function NewRecipientWizard() {
  const [isBusy, setBusy] = useState(false)
  const [step, setStep] = useState(steps.method)
  const [selected, setSelected] = useState(methods[0])
  const nameRef = useRef<string | null>(null)
  const emailRef = useRef<string | null>(null)
  const phoneRef = useRef<PhoneInputData | null>(null)

  const router = useRouter()
  const { note_id, recipients } = useNote()

  const addNewRecipient = useCallback(() => {
    const email = emailRef?.current
    const phone = phoneRef?.current

    if (selected.id == 'email' && (!email || email?.length < 6)) {
      // a@a.bb = 6
      toast.error('Please provide a valid email address')
      return
    }

    if (selected.id == 'phone' && !phone?.isPossible) {
      toast.error('Please provide a valid phone number')
      return
    }

    if (isExistingRecipient(recipients, email, phone?.full_phone)) {
      toast.error(`This note already has a recipient with this ${selected?.id}`)
      return
    }

    handleAPIRequest(
      localAPIRequest({
        data: {
          note_id,
          recipient: {
            email: email,
            phone: phone?.local,
            name: nameRef?.current,
            deliver_by: selected.id,
            full_phone: phone?.full_phone,
            country: phone?.country?.code,
            country_code: phone?.country?.dial_code
              ? parseInt(phone?.country?.dial_code)
              : null,
          },
        },
        action: 'addRecipient',
      }),
      {
        busyState: setBusy,
        successCallback: (data) => {
          logEvent(EVENTS.noteRecipientAdded)
          setTimeout(() => {
            router.push(
              createRecipientLinkWithID(note_id, 'recipients', data?.id)
            )
          }, 250)
        },
        successMessage: 'Recipient added!',
        loadingMessage: 'Adding note recipient',
        errorMessage:
          'Sorry, we could not add the recipient. Please try again.',
      }
    )
  }, [note_id, selected, recipients, router])

  const Footer = ({
    text,
    backStep,
    backBtnText,
    children,
  }: {
    text?: string
    backStep?: number
    backBtnText?: string
  } & PropsWithChildren) => (
    <div className="mt-8 md:flex md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        {text?.length ? <small>{text}</small> : ''}
      </div>
      <div className="flex md:ml-4">
        <div className="space-x-2">
          {backBtnText?.length ? (
            <ButtonWithIcon onClick={() => setStep(backStep as number)}>
              {backBtnText}
            </ButtonWithIcon>
          ) : null}
          {children}
        </div>
      </div>
    </div>
  )

  const Step2PhoneOrEmail = () => {
    return (
      <div>
        <PageSection className="">
          <div className="flex justify-between px-4 py-2 border-b border-color-only">
            <div className="flex flex-col justify-center">
              Deliver note {selected.label}
            </div>
            <ButtonWithIcon mini onClick={() => setStep(steps.method)}>
              Change
            </ButtonWithIcon>
          </div>
          <div className="p-4 space-y-4">
            <NameInput nameRef={nameRef} />
            {selected?.id == 'email' ? (
              <EmailInput emailRef={emailRef} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2">
                <label
                  htmlFor="phone"
                  className="text-sm flex flex-col justify-center w-36"
                >
                  Their Phone
                </label>
                <PhoneInput refValue={phoneRef} />
              </div>
            )}
          </div>
          <SectionFooter
            leftContent={
              <small>You can update or replace this recipient later.</small>
            }
            rightContent={
              <ButtonWithIcon
                Icon={PlusIcon}
                color={'green'}
                isDisabled={isBusy}
                onClick={addNewRecipient}
              >
                Add Recipient
              </ButtonWithIcon>
            }
          ></SectionFooter>
        </PageSection>
      </div>
    )
  }

  return (
    <div className="">
      {step == 1 && (
        <div>
          <p className="text-lg mb-4">How should they receive your note?</p>
          <Step1Method value={selected} onChange={setSelected} />
          <Footer
            text="You can update these option later. Your note will be sent as a
                PDF file."
          >
            <ButtonWithIcon
              color={'blue'}
              onClick={() => {
                setStep(steps.contactInfo)
              }}
            >
              Next
            </ButtonWithIcon>
          </Footer>
        </div>
      )}

      {step == 2 && selected && <Step2PhoneOrEmail />}
    </div>
  )
}

function AddNoteRecipients() {
  const recipientCandidates = [
    'Family; Parents, Marriage partner, Adult Kids, Siblings',
    'Your lawyer',
    'Trusted friends',
    'Business partners or colleagues if this is a business note',
    'Anyone else who needs the information in the note, or will help with it',
  ]

  return (
    <div className="pb-8">
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 lg:gap-8">
        <div className="grid grid-cols-1 gap-4 lg:col-span-2">
          <PageHeader heading="Add a Recipient"></PageHeader>
          <NewRecipientWizard />
        </div>

        <div className="grid grid-cols-1 gap-4">
          <PageSection>
            <div className="px-4 py-3">
              <h4 className="text-lg font-medium flex flex-col items-center flex-wrap">
                <UserGroupIcon className="w-6 h-6 text-blue-500 ml-2 mb-2 font-semibold" />
                who could be a recipient
              </h4>
              <ul className="list-disc mt-4" role="list">
                {recipientCandidates.map((text, i) => (
                  <li className="mb-4 flex items-start" key={i}>
                    <InformationCircleIcon className="mt-1 w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                    <span className="">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </PageSection>
        </div>
      </div>
    </div>
  )
}

AddNoteRecipients.getLayout = function GetLayout(page: ReactElement) {
  const router = useRouter()
  const note_id = router?.query?.id as string

  return (
    <NoteLayout note_id={note_id} title="Add a Recipient" width={4}>
      {page}
    </NoteLayout>
  )
}

export default AddNoteRecipients
